/**
 * AutomationControls
 * ------------------
 * Global kill switch for AUTOMATED hospital emails (the daily 8:00 AM CT
 * tracking sweep), plus the one-time cleanup that strips bogus auto-generated
 * shipment entries. Manual sends (buttons you click) are never affected.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldAlert, ShieldCheck, Loader2, Eraser } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AutomationControls({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/tracking/settings`, { headers });
      setSettings(r.data);
    } catch (e) {
      /* non-blocking */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const paused = !!settings?.automated_hospital_emails_paused;

  const toggle = async () => {
    const next = !paused;
    if (next && !window.confirm("Pause ALL automated hospital emails? Manual sends still work.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/tracking/pause`, { paused: next }, { headers });
      toast[next ? "warning" : "success"](
        next ? "Automated hospital emails are PAUSED" : "Automated hospital emails resumed",
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change the setting");
    } finally {
      setBusy(false);
    }
  };

  const purge = async () => {
    if (!window.confirm("Remove auto-generated shipment entries from order timelines and restore their status? This cannot be undone.")) return;
    setPurging(true);
    try {
      const r = await axios.post(`${API}/api/admin/tracking/purge-auto-notices`, {}, { headers });
      toast.success(
        `Cleanup done — ${r.data?.orders_cleaned || 0} order(s), ${r.data?.milestones_removed || 0} entr${(r.data?.milestones_removed || 0) === 1 ? "y" : "ies"} removed`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setPurging(false);
    }
  };

  return (
    <div
      className={`rounded-lg border p-3 flex flex-col sm:flex-row sm:items-center gap-3 ${
        paused ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-white"
      }`}
      data-testid="automation-controls"
    >
      <div className="flex-1">
        <p className="text-sm font-bold text-slate-800 inline-flex items-center gap-2">
          {paused
            ? <ShieldAlert className="w-4 h-4 text-rose-600" />
            : <ShieldCheck className="w-4 h-4 text-emerald-600" />}
          Automated hospital emails: {paused ? "PAUSED" : "Active"}
        </p>
        <p className="text-[11px] text-slate-500 mt-0.5">
          {paused
            ? `Paused${settings?.paused_by ? ` by ${settings.paused_by}` : ""} — the daily sweep will not email anyone. Buttons you click still send.`
            : "Daily sweep at 8:00 AM CT · ship, delay and delivery only · selected recipients only · shipments entered after 25 Sep 2026."}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          variant="outline"
          onClick={purge}
          disabled={purging}
          title="Strip auto-generated shipment entries created by the old tracking job"
          data-testid="automation-purge-btn"
        >
          {purging ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Eraser className="w-3.5 h-3.5 mr-1.5" />}
          Clean up auto entries
        </Button>
        <Button
          size="sm"
          onClick={toggle}
          disabled={busy || settings === null}
          className={paused ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}
          data-testid="automation-pause-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
          {paused ? "Resume automated emails" : "Pause automated emails"}
        </Button>
      </div>
    </div>
  );
}
